import { playerAppearance } from '../animation/Theme';
import { saveSystem } from '../systems/SaveSystem';
import type { PlayerInput } from '../multiplayer/PlayerInput';
import { DirectionalSpriteController } from '../animation/DirectionalSpriteController';
import Phaser from 'phaser';
import {
  TILE,
  PLAYER_SPEED_BASE,
  SPEED_POWERUP_INCREMENT,
  PLAYER_SPEED_MAX_MULT,
  PLAYER_START_BOMBS,
  PLAYER_START_RADIUS,
  INVULNERABLE_MS,
  PLAYER_COLORS,
  MAP_COLS,
  MAP_ROWS,
} from '../constants';
import { CellType, PowerUpType, type PlayerStats } from '../types';

// Grid type: 2D array of CellType values
export type Grid = CellType[][];

// Direction vectors
const DIR_UP    = { dx:  0, dy: -1 };
const DIR_DOWN  = { dx:  0, dy:  1 };
const DIR_LEFT  = { dx: -1, dy:  0 };
const DIR_RIGHT = { dx:  1, dy:  0 };

// Input key bindings per player
interface KeyBindings {
  up:    Phaser.Input.Keyboard.Key;
  down:  Phaser.Input.Keyboard.Key;
  left:  Phaser.Input.Keyboard.Key;
  right: Phaser.Input.Keyboard.Key;
  bomb:  Phaser.Input.Keyboard.Key;
}

// Pixel interpolation state
interface MoveState {
  col:       number; // current logical column
  row:       number; // current logical row
  targetCol: number; // destination column
  targetRow: number; // destination row
  pixelX:    number; // rendered pixel X (centre of tile)
  pixelY:    number; // rendered pixel Y (centre of tile)
  moving:    boolean;
}

export class Player extends Phaser.GameObjects.Container {
  // Identity
  readonly playerId: number;

  // Stats
  readonly stats: PlayerStats;

  // Movement
  private move: MoveState;
  private facingDx: number = 0;
  private facingDy: number = 1; // default faces down

  // Bomb tracking
  private readonly activeBombs: Set<string> = new Set();

  // Input
  private readonly keys: KeyBindings;
  private dying = false;
  get isDying(): boolean { return this.dying; }

  private touchInput: Readonly<PlayerInput> | undefined;
  private bombJustPressed: boolean = false;
  private remoteJustPressed = false;
  private remoteKey: Phaser.Input.Keyboard.Key;
  private bombBlocksCell: (col: number, row: number) => boolean = () => false;
  onKickBomb: ((col: number, row: number, dx: number, dy: number) => boolean) | null = null;
  onRemoteDetonate: (() => boolean) | null = null;

  // Invulnerability
  private invulnerableTimer: number = 0;
  private blinkTimer:        number = 0;
  private readonly BLINK_INTERVAL = 150; // ms

  // Sprite children
  private sprite: Phaser.GameObjects.Sprite;
  private visual: DirectionalSpriteController;

  // Grid reference (injected by the scene / map system)
  private grid: Grid | null = null;

  // Bomb placement callback (set by GameScene)
  onPlaceBomb: ((col: number, row: number) => boolean) | null = null;

  constructor(
    scene:    Phaser.Scene,
    col:      number,
    row:      number,
    playerId: number,
  ) {
    const px = col * TILE + TILE / 2;
    const py = row * TILE + TILE / 2;
    super(scene, px, py);

    this.playerId = playerId;

    // ---- Stats ----
    this.stats = {
      maxBombs:         PLAYER_START_BOMBS,
      blastRadius:      PLAYER_START_RADIUS,
      speedMult:        1.0,
      lives:            3,
      activePowerUps:   new Set<PowerUpType>(),
      hasShield:        false,
      hasBombKick:      false,
      hasRemoteBomb:    false,
      hasWallPass:      false,
      hasBombPass:      false,
      fireImmunityTimer: 0,
    };

    // ---- Movement state ----
    this.move = {
      col,
      row,
      targetCol: col,
      targetRow: row,
      pixelX:    px,
      pixelY:    py,
      moving:    false,
    };

    // ---- Input bindings ----
    const kb = scene.input.keyboard!;
    const controls = saveSystem.settings.controls;
    if (playerId === 0) {
      this.keys = {
        up:    kb.addKey(controls.p1Up),
        down:  kb.addKey(controls.p1Down),
        left:  kb.addKey(controls.p1Left),
        right: kb.addKey(controls.p1Right),
        bomb:  kb.addKey(controls.p1Bomb),
      };
    } else if (playerId === 1) {
      this.keys = {
        up:    kb.addKey(controls.p2Up),
        down:  kb.addKey(controls.p2Down),
        left:  kb.addKey(controls.p2Left),
        right: kb.addKey(controls.p2Right),
        bomb:  kb.addKey(controls.p2Bomb),
      };
    } else if (playerId === 2) {
      this.keys = {
        up:    kb.addKey(Phaser.Input.Keyboard.KeyCodes.I),
        down:  kb.addKey(Phaser.Input.Keyboard.KeyCodes.K),
        left:  kb.addKey(Phaser.Input.Keyboard.KeyCodes.J),
        right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.L),
        bomb:  kb.addKey(Phaser.Input.Keyboard.KeyCodes.U),
      };
    } else {
      this.keys = {
        up:    kb.addKey(Phaser.Input.Keyboard.KeyCodes.T),
        down:  kb.addKey(Phaser.Input.Keyboard.KeyCodes.G),
        left:  kb.addKey(Phaser.Input.Keyboard.KeyCodes.F),
        right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.H),
        bomb:  kb.addKey(Phaser.Input.Keyboard.KeyCodes.R),
      };
    }

    this.remoteKey = kb.addKey([
      Phaser.Input.Keyboard.KeyCodes.E, Phaser.Input.Keyboard.KeyCodes.SHIFT,
      Phaser.Input.Keyboard.KeyCodes.O, Phaser.Input.Keyboard.KeyCodes.Y,
    ][playerId]);

    // ---- Sprite ----
    this.add(scene.add.ellipse(0, 19, 32, 10, 0x000000, 0.22));
    const appearance = playerAppearance();
    this.sprite = scene.add.sprite(0, 0, appearance.texture, appearance.frame);
    this.add(this.sprite);
    this.visual = new DirectionalSpriteController(this.sprite, appearance.character);
    scene.add.existing(this);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /** Provide the current grid so the player can check passability. */
  setGrid(grid: Grid): void {
    this.grid = grid;
  }

  setBombCollisionQuery(query: (col: number, row: number) => boolean): void { this.bombBlocksCell = query; }

  onBombMoved(col: number, row: number, nextCol: number, nextRow: number): void {
    if (this.activeBombs.delete(`${col},${row}`)) this.activeBombs.add(`${nextCol},${nextRow}`);
  }

  getGridPos(): { col: number; row: number } {
    return { col: this.move.col, row: this.move.row };
  }

  /** True when the player has no lives left. */
  get isDead(): boolean {
    return this.stats.lives <= 0;
  }

  get isInvulnerable(): boolean {
    return this.invulnerableTimer > 0;
  }

  /** Main update — call from scene's update(). */
  update(delta: number, touchInput?: Readonly<PlayerInput>): void {
    this.touchInput = touchInput;
    if (this.isDead || this.dying) return;

    this.updateInvulnerability(delta);
    this.updateFireImmunity(delta);
    this.updateMovement(delta);
    this.updateBombInput();
    this.updateWalkAnimation(delta);
  }

  private updateWalkAnimation(delta: number): void {
    const direction = this.facingDy < 0 ? 'up' : this.facingDy > 0 ? 'down' : this.facingDx < 0 ? 'left' : 'right';
    this.visual.update(delta, this.move.moving, direction);
    this.sprite.anims.timeScale = this.move.moving ? this.stats.speedMult : 1;
  }

  /**
   * Attempt to place a bomb at the current grid position.
   * Returns true if placement was accepted (caller should create Bomb entity).
   */
  celebrate(): void { this.visual.action('victory', 1500); this.visual.update(0, false); }

  placeBomb(): boolean {
    if (this.isDead || this.dying || !this.canPlaceBomb()) return false;
    const key = `${this.move.col},${this.move.row}`;
    if (this.activeBombs.has(key)) return false; // already a bomb here
    this.activeBombs.add(key);
    if (this.onPlaceBomb?.(this.move.col, this.move.row) !== true) {
      this.activeBombs.delete(key);
      return false;
    }
    this.visual.action('place-bomb');
    return true;
  }

  /**
   * Called by BombSystem when one of this player's bombs explodes.
   */
  onBombExploded(col: number, row: number): void {
    this.activeBombs.delete(`${col},${row}`);
  }

  /**
   * Apply damage. Returns true if the player actually lost a life.
   */
  takeDamage(source: 'explosion' | 'enemy' = 'explosion'): boolean {
    if (this.isDead || this.dying) return false;
    if (this.invulnerableTimer > 0) return false;
    if (this.stats.hasShield) {
      this.stats.hasShield = false;
      this.stats.activePowerUps.delete(PowerUpType.SHIELD);
      this.startInvulnerability();
      return false;
    }
    if (source === 'explosion' && this.stats.fireImmunityTimer > 0) return false;

    this.stats.lives -= 1;
    this.dying = true;
    this.visual.action('death', 1200); this.visual.update(0, false);
    if (this.stats.lives > 0) {
      this.startInvulnerability();
    }
    return true;
  }

  /**
   * Respawn the player at a new grid position.
   */
  respawn(col: number, row: number): void {
    this.dying = false;
    this.visual.action('idle', 0);
    this.move.col       = col;
    this.move.row       = row;
    this.move.targetCol = col;
    this.move.targetRow = row;
    this.move.pixelX    = col * TILE + TILE / 2;
    this.move.pixelY    = row * TILE + TILE / 2;
    this.move.moving    = false;
    this.setPosition(this.move.pixelX, this.move.pixelY);
    this.setVisible(true);
    this.startInvulnerability();
  }

  /**
   * Apply a power-up to this player.
   */
  addPowerUp(type: PowerUpType): void {
    this.stats.activePowerUps.add(type);
    switch (type) {
      case PowerUpType.EXTRA_BOMB:
        this.stats.maxBombs = Math.min(this.stats.maxBombs + 1, 8);
        break;
      case PowerUpType.FIRE_UP:
        this.stats.blastRadius = Math.min(this.stats.blastRadius + 1, 9);
        break;
      case PowerUpType.SPEED_UP:
        this.stats.speedMult = Math.min(this.stats.speedMult + SPEED_POWERUP_INCREMENT, PLAYER_SPEED_MAX_MULT);
        break;
      case PowerUpType.BOMB_KICK:
        this.stats.hasBombKick = true;
        break;
      case PowerUpType.REMOTE_BOMB:
        this.stats.hasRemoteBomb = true;
        break;
      case PowerUpType.SHIELD:
        this.stats.hasShield = true;
        break;
      case PowerUpType.EXTRA_LIFE:
        this.stats.lives = Math.min(this.stats.lives + 1, 9);
        break;
      case PowerUpType.FIRE_IMMUNITY:
        this.stats.fireImmunityTimer = 8000; // 8 seconds
        break;
      case PowerUpType.WALL_PASS:
        this.stats.hasWallPass = true;
        break;
      case PowerUpType.BOMB_PASS:
        this.stats.hasBombPass = true;
        break;
    }
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private canPlaceBomb(): boolean {
    return this.activeBombs.size < this.stats.maxBombs;
  }

  private startInvulnerability(): void {
    this.invulnerableTimer = INVULNERABLE_MS;
    this.blinkTimer        = 0;
    this.setVisible(true);
  }

  private updateInvulnerability(delta: number): void {
    if (this.invulnerableTimer <= 0) {
      this.setVisible(true);
      return;
    }
    this.invulnerableTimer -= delta;
    this.blinkTimer        += delta;

    if (this.blinkTimer >= this.BLINK_INTERVAL) {
      this.blinkTimer -= this.BLINK_INTERVAL;
      this.setVisible(!this.visible);
    }

    if (this.invulnerableTimer <= 0) {
      this.invulnerableTimer = 0;
      this.setVisible(true);
    }
  }

  private updateFireImmunity(delta: number): void {
    if (this.stats.fireImmunityTimer > 0) {
      this.stats.fireImmunityTimer = Math.max(0, this.stats.fireImmunityTimer - delta);
      if (this.stats.fireImmunityTimer === 0) this.stats.activePowerUps.delete(PowerUpType.FIRE_IMMUNITY);
    }
  }

  private updateMovement(delta: number): void {
    const m = this.move;
    for (const [key, dx, dy] of [[this.keys.up, 0, -1], [this.keys.down, 0, 1], [this.keys.left, -1, 0], [this.keys.right, 1, 0]] as const) {
      if (Phaser.Input.Keyboard.JustDown(key)) { this.facingDx = dx; this.facingDy = dy; }
    }

    if (this.touchInput) {
      const touch = this.touchInput;
      if (touch.up || touch.down || touch.left || touch.right) {
        this.facingDx = touch.left ? -1 : touch.right ? 1 : 0;
        this.facingDy = touch.up ? -1 : touch.down ? 1 : 0;
      }
    }

    if (m.moving && this.touchInput) {
      const touch=this.touchInput;
      const dx=touch.left?-1:touch.right?1:0, dy=touch.up?-1:touch.down?1:0;
      const toX=m.targetCol*TILE+TILE/2-m.pixelX,toY=m.targetRow*TILE+TILE/2-m.pixelY;
      if ((dx || dy) && dx*toX+dy*toY < -0.01 && (dx===0?Math.abs(toX)<0.01:Math.abs(toY)<0.01)) {
        if (m.targetCol!==m.col || m.targetRow!==m.row) { m.targetCol=m.col; m.targetRow=m.row; }
        else if (this.isCellPassable(m.col+dx,m.row+dy)) { m.targetCol=m.col+dx; m.targetRow=m.row+dy; }
      }
    }

    // Consume the complete distance budget, including time left after reaching a cell.
    // Dropping that time and waiting a frame between cells muted boosts on slow phones.
    let budget = PLAYER_SPEED_BASE * this.stats.speedMult * Math.max(0, delta) / 1000;
    for (let steps = 0; steps < MAP_COLS + MAP_ROWS; steps++) {
      if (!m.moving) {
        let dx = 0, dy = 0;
        if (this.keys.up.isDown || this.touchInput?.up) dy = -1;
        else if (this.keys.down.isDown || this.touchInput?.down) dy = 1;
        else if (this.keys.left.isDown || this.touchInput?.left) dx = -1;
        else if (this.keys.right.isDown || this.touchInput?.right) dx = 1;
        if (!dx && !dy) break;
        this.facingDx = dx; this.facingDy = dy;
        if (!this.isCellPassable(m.col + dx, m.row + dy)) break;
        m.targetCol = m.col + dx; m.targetRow = m.row + dy; m.moving = true;
      }
      if (budget <= 0) break;
      const targetX = m.targetCol * TILE + TILE / 2, targetY = m.targetRow * TILE + TILE / 2;
      const dx = targetX - m.pixelX, dy = targetY - m.pixelY;
      const remaining = Math.hypot(dx, dy);
      if (remaining <= budget) {
        m.pixelX = targetX; m.pixelY = targetY;
        m.col = m.targetCol; m.row = m.targetRow; m.moving = false;
        budget -= remaining;
      } else {
        m.pixelX += dx * budget / remaining; m.pixelY += dy * budget / remaining;
        budget = 0;
      }
    }
    this.setPosition(m.pixelX, m.pixelY);
  }

  private updateBombInput(): void {
    const remoteDown = this.remoteKey.isDown || Boolean(this.touchInput?.detonate);
    if (remoteDown && !this.remoteJustPressed && this.stats.hasRemoteBomb) this.onRemoteDetonate?.();
    this.remoteJustPressed = remoteDown;
    const isDown = this.keys.bomb.isDown || Boolean(this.touchInput?.bomb);
    if (isDown && !this.bombJustPressed) {
      this.bombJustPressed = true;
      this.placeBomb();
    } else if (!isDown) {
      this.bombJustPressed = false;
    }
  }

  /**
   * Returns true if the grid cell at (col, row) can be entered.
   */
  private isCellPassable(col: number, row: number): boolean {
    // Bounds check
    if (col < 0 || col >= MAP_COLS || row < 0 || row >= MAP_ROWS) return false;

    if (!this.grid) return true; // no grid yet — allow movement

    const cell = this.grid[row]?.[col];
    if (cell === undefined) return false;

    if (cell === CellType.WALL)  return false;
    if (cell === CellType.BLOCK && !this.stats.hasWallPass) return false;

    // Bomb cell: blocked unless hasBombPass
    if (!this.stats.hasBombPass && (this.bombBlocksCell(col, row) || this.activeBombs.has(`${col},${row}`))) {
      const dx = col - this.move.col, dy = row - this.move.row;
      return this.stats.hasBombKick && this.onKickBomb?.(col, row, dx, dy) === true;
    }

    return true;
  }

}

export default Player;
